import {Component, FC, lazy, useEffect, useMemo, useState} from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import PrivateLayout from '../components/PrivateLayout';
import PublicLayout from '../components/PublicLayout';
import DownloadMobileApp from '../pages/DownloadMobileApp/DownloadMobileApp';
import PhoneVerify from '../pages/SignUp/Personal/PhoneVerify/PhoneVerify';
import Profile from '../pages/SignUp/Personal/Profile/Profile';
import BusinessContactUs from '../pages/SignUp/business/BusinessContactUs/BusinessContactUs';
import CompanyEmails from '../pages/SignUp/business/CompanyEmails/CompanyEmails';
import Email from '../pages/SignUp/business/Email/Email';
import BusinessPhone from '../pages/SignUp/business/Phone/Phone';
import BusinessPhoneVerify from '../pages/SignUp/business/PhoneVerify/PhoneVerify';
import { Crm } from '../pages/CRM/Crm';
import { CrmConnect } from '../pages/CRM/CrmConnect/CrmConnect';
import { CrmToken } from '../pages/CRM/CrmToken/CrmToken';
import ContactUs from "../pages/SignUp/Personal/ContactUs/ContactUs";

const Error404 = lazy<FC>(() => import('../pages/Error404/Error404'));

const Home = lazy<FC>(() => import('../pages/Home/Home'));
const Map = lazy<FC>(() => import('../pages/Map/Map'));

const Login = lazy<FC>(() => import('../pages/Login/Login'));
const SignUp = lazy<FC>(() => import('../pages/SignUp/SignUp'));
const Welcome = lazy<FC>(() => import("../pages/Welcome/Welcome"));
const Personal = lazy<FC>(() => import("../pages/SignUp/Personal/Personal"));
const Business = lazy<FC>(() => import("../pages/SignUp/business/Business"));
const Phone = lazy<FC>(() => import("../pages/SignUp/Personal/Phone/Phone"));
const CompanyMembers = lazy<FC>(() => import("../pages/CompanyMembers/CompanyMembers"));
const CreateCustomer = lazy<FC>(() => import('../pages/Home/components/CreateCustomer/CreateCustomer'));
const CustomerDetails = lazy<FC>(() => import('../pages/CustomerDetails/CustomerDetails'));

const History = lazy<FC>(() => import('../pages/History/History'));
const useRoutes = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const privateRoutes = useMemo<RouteObject[]>(
    () =>
      isMobile
        ? [
            {
              path: '*',
              Component: () => <DownloadMobileApp />
            }
          ]
        : [
            {
              Component: PrivateLayout,
              children: [
                {
                  path: '',
                  children: [
                    {
                      Component: Home,
                      index: true
                    }
                  ]
                },
                {
                  path: 'map',
                  children: [
                    {
                      Component: Map,
                      index: true
                    }
                  ]
                },
                {
                  path: 'contact-us',
                  children: [
                    {
                      Component: ContactUs,
                      index: true
                    }
                  ]
                },
                {
                  path: 'company-members',
                  children: [
                    {
                      Component: CompanyMembers,
                      index: true
                    }
                  ]
                },
                {
                  path: 'history',
                  children: [
                    {
                      Component: History,
                      index: true
                    }
                  ]
                },
                {
                  path: 'create-customer',
                  children: [
                    {
                      Component: CreateCustomer,
                      index: true
                    }
                  ]
                },
                {
                  path: 'customer-details/:id',
                  children: [
                    {
                      Component: CustomerDetails,
                      index: true
                    }
                  ]
                },
                {
                  path: 'crm',
                  children: [
                    {
                      Component: Crm,
                      index: true
                    },
                    {
                      path: 'connect',
                      Component: CrmConnect
                    },
                    {
                      path: 'token',
                      Component: CrmToken
                    }
                  ]
                }
              ]
            }
          ],
    []
  );

  const publicRoutes = useMemo<RouteObject[]>(
    () =>
      isMobile
        ? [
            {
              path: '*',
              Component: () => <DownloadMobileApp />
            }
          ]
        : [
            {
              element: <Navigate replace to="/" />,
              path: '*'
            },
            {
              Component: Welcome,
              path: ''
            },

            {
              Component: PublicLayout,
              children: [
                {
                  path: 'login',
                  children: [
                    {
                      Component: Login,
                      index: true
                    }
                  ]
                },
                {
                  path: 'sign-up',
                  children: [
                    {
                      Component: SignUp,
                      index: true
                    },
                    {
                      Component: Personal,
                      path: 'personal'
                    },
                    {
                      Component: Phone,
                      path: 'personal/phone'
                    },
                    {
                      Component: PhoneVerify,
                      path: 'personal/phone-verify'
                    },
                    {
                      Component: Profile,
                      path: 'personal/profile'
                    },
                    {
                      Component: ContactUs,
                      path: 'personal/contact-us'
                    },
                    {
                      Component: Business,
                      path: 'business'
                    },
                    {
                      Component: BusinessContactUs,
                      path: 'business/contact-us'
                    },
                    {
                      Component: CompanyEmails,
                      path: 'business/company-emails'
                    },
                    {
                      Component: Email,
                      path: 'business/email'
                    },
                    {
                      Component: BusinessPhone,
                      path: 'business/phone'
                    },
                    {
                      Component: BusinessPhoneVerify,
                      path: 'business/phone-verify'
                    }
                  ]
                }
              ]
            }
          ],
    []
  );

  return { privateRoutes, publicRoutes };
};

export default useRoutes;
